import React from "react";
import { makeStyles, Tooltip } from "@material-ui/core";
import { Check } from "@material-ui/icons";
import { PayStatus } from "types/player";

type BadgeSize = "default" | "compact" | "presentation";

const useStyles = makeStyles(theme => ({
  root: {
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    verticalAlign: "middle",
  },
  rootDefault: {
    marginLeft: theme.spacing(0.75),
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    letterSpacing: "0.01em",
    lineHeight: 1,
  },
  chipDefault: {
    height: 24,
    minWidth: 24,
    padding: theme.spacing(0, 1),
    borderRadius: 12,
    gap: theme.spacing(0.375),
    fontSize: "0.6875rem",
    fontWeight: 600,
  },
  chipCompact: {
    height: 18,
    minWidth: 18,
    padding: theme.spacing(0, 0.5),
    borderRadius: 9,
    gap: theme.spacing(0.25),
    fontSize: "0.625rem",
    fontWeight: 600,
  },
  chipPresentation: {
    height: 28,
    minWidth: 28,
    padding: theme.spacing(0, 0.75),
    borderRadius: 14,
    gap: theme.spacing(0.375),
    fontSize: "0.8125rem",
    fontWeight: 600,
  },
  chipSymbolDefault: {
    fontSize: "0.75rem",
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
  },
  chipSymbolCompact: {
    fontSize: "0.625rem",
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
  },
  chipSymbolCompactIcon: {
    fontSize: "0.8125rem",
    fontWeight: 800,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
  },
  chipSymbolPresentation: {
    fontSize: "1rem",
    fontWeight: 800,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
  },
  chipIconDefault: {
    fontSize: "0.8125rem",
  },
  chipIconCompact: {
    fontSize: "0.6875rem",
  },
  unpaid: {
    backgroundColor: "#f6e7e7",
    color: "#d20000",
  },
  guest: {
    backgroundColor: "#E8F0FE",
    color: "#1E5AA8",
  },
  paid: {
    backgroundColor: "#E8F5E9",
    color: "#2E7D32",
  },
  labeledDefault: {
    padding: theme.spacing(0, 1.25),
  },
  labeledCompact: {
    padding: theme.spacing(0, 0.75),
  },
  labeledPresentation: {
    padding: theme.spacing(0, 1.25),
  },
}));

const BADGE_TOOLTIPS: Record<"unpaid" | "guest" | "paid", string> = {
  unpaid: "Unpaid — payment not received",
  guest: "Guest player",
  paid: "Paid",
};

interface Props {
  payStatus?: PayStatus | number;
  className?: string;
  size?: BadgeSize;
  /** Show green Paid chip. Default false — leaderboard, start sheet, players list only flag unpaid/guest. */
  showPaid?: boolean;
  /** Show text labels (Unpaid, Guest, Paid). Use in payment and guest management. */
  showLabels?: boolean;
}

/** Unified pill badges for payment status — default size for payment flows, compact for dense tables. */
const PaymentStatusBadge: React.FC<Props> = ({
  payStatus,
  className,
  size = "default",
  showPaid = false,
  showLabels = false,
}) => {
  const classes = useStyles();
  const status = Number(payStatus);
  const isCompact = size === "compact";
  const isPresentation = size === "presentation";
  const rootClass = [
    classes.root,
    !isCompact && !isPresentation ? classes.rootDefault : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const chipClass = [
    classes.chip,
    isPresentation ? classes.chipPresentation : isCompact ? classes.chipCompact : classes.chipDefault,
  ].join(" ");
  const symbolClass = isPresentation
    ? classes.chipSymbolPresentation
    : isCompact
      ? showLabels
        ? classes.chipSymbolCompact
        : classes.chipSymbolCompactIcon
      : classes.chipSymbolDefault;
  const iconClass = isPresentation
    ? classes.chipIconDefault
    : isCompact
      ? classes.chipIconCompact
      : classes.chipIconDefault;
  const labeledClass = showLabels
    ? isPresentation
      ? classes.labeledPresentation
      : isCompact
        ? classes.labeledCompact
        : classes.labeledDefault
    : "";
  const paidPadding = isPresentation
    ? classes.labeledPresentation
    : isCompact
      ? classes.labeledCompact
      : classes.labeledDefault;

  const wrapWithTooltip = (tooltip: string, ariaLabel: string, badgeClass: string, content: React.ReactNode) => (
    <Tooltip title={tooltip} arrow>
      <span className={`${rootClass} ${chipClass} ${badgeClass}`} aria-label={ariaLabel}>
        {content}
      </span>
    </Tooltip>
  );

  if (status === PayStatus.NOT_PAID) {
    return wrapWithTooltip(
      BADGE_TOOLTIPS.unpaid,
      "Unpaid",
      `${classes.unpaid} ${labeledClass}`,
      <>
        <span className={symbolClass}>£</span>
        {showLabels ? "Unpaid" : null}
      </>,
    );
  }

  if (status === PayStatus.GUEST) {
    return wrapWithTooltip(
      BADGE_TOOLTIPS.guest,
      "Guest",
      `${classes.guest} ${labeledClass}`,
      <>{showLabels ? "Guest" : <span className={symbolClass}>G</span>}</>,
    );
  }

  if (status === PayStatus.PAID && showPaid) {
    return wrapWithTooltip(
      BADGE_TOOLTIPS.paid,
      "Paid",
      `${classes.paid} ${paidPadding}`,
      <>
        <Check className={iconClass} />
        Paid
      </>,
    );
  }

  return null;
};

export default PaymentStatusBadge;
